import {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
  type RefObject,
} from 'react';
import type React from 'react';
import { createPortal } from 'react-dom';
import { twMerge } from 'tailwind-merge';


import { Stack } from 'ui/stack';

import Button from 'components/partials/button/button';
import MessageInput from './message-input';

import {
  AI_ASSISTANT_CHAT_MESSAGE_MAX_WIDTH_CLASS,
  FEEDBACK_TYPES,
  FEEDBACK_OFFSET_TOP,
  FEEDBACK_HEIGHT_ESTIMATE,
} from '../../ai-assistant.constants';

import Icons from 'assets/icons';

interface FeedbackPosition {
  top: number;
  left: number;
  width: number;
}

const getScrollParent = (el: Element | null): HTMLElement | null => {
  let parent = el?.parentElement ?? null;

  while (parent) {
    const { overflowY } = getComputedStyle(parent);

    if (
      ['auto', 'scroll', 'overlay'].includes(overflowY) &&
      parent.scrollHeight > parent.clientHeight
    ) {
      return parent;
    }
    parent = parent.parentElement;
  }

  return null;
};

interface TriggerCloseRef {
  current: (() => void) | null;
}

interface FeedbackSectionProps {
  onClose: () => void;
  onSendFeedback: (type: string | null, message: string) => void;
  isSendingFeedback: boolean;
  feedbackType: string | null;
  anchorRef: RefObject<HTMLElement | null>;
  triggerRef: RefObject<HTMLElement | null> | TriggerCloseRef;
}

const FeedbackSection = ({
  onClose,
  onSendFeedback,
  isSendingFeedback,
  feedbackType,
  anchorRef,
  triggerRef,
}: FeedbackSectionProps): React.ReactPortal => {
  const [position, setPosition] = useState<FeedbackPosition | null>(null);
  const sectionRef = useRef<HTMLDivElement>(null);
  const scrollParentRef = useRef<HTMLElement | null>(null);

  const feedbackTypeLabel = useMemo(
    () =>
      feedbackType === FEEDBACK_TYPES.POSITIVE
        ? 'What did you like about this response?'
        : `What didn't you like about this response?`,
    [feedbackType]
  );

  const updatePosition = useCallback(() => {
    if (!anchorRef?.current) return;

    const anchorRect = anchorRef.current.getBoundingClientRect();
    const container = scrollParentRef.current;

    let top = anchorRect.bottom + FEEDBACK_OFFSET_TOP;

    if (container) {
      const containerRect = container.getBoundingClientRect();
      const minTop = containerRect.top + FEEDBACK_OFFSET_TOP;
      const maxTop = containerRect.bottom - FEEDBACK_HEIGHT_ESTIMATE;

      top = Math.max(minTop, Math.min(top, maxTop));
    }

    setPosition({ top, left: anchorRect.left, width: anchorRect.width });
  }, [anchorRef]);

  useEffect(() => {
    const scrollEl = getScrollParent(anchorRef?.current ?? null);

    scrollParentRef.current = scrollEl;

    if (scrollEl && anchorRef?.current) {
      const anchorRect = anchorRef.current.getBoundingClientRect();
      const containerRect = scrollEl.getBoundingClientRect();
      const spaceBelow = containerRect.bottom - anchorRect.bottom;

      if (spaceBelow < FEEDBACK_HEIGHT_ESTIMATE + FEEDBACK_OFFSET_TOP) {
        const scrollBy =
          FEEDBACK_HEIGHT_ESTIMATE + FEEDBACK_OFFSET_TOP - spaceBelow;

        scrollEl.scrollBy({ top: scrollBy, behavior: 'smooth' });
      }
    }

    requestAnimationFrame(updatePosition);

    scrollEl?.addEventListener('scroll', updatePosition);
    window.addEventListener('resize', updatePosition);

    return () => {
      scrollEl?.removeEventListener('scroll', updatePosition);
      window.removeEventListener('resize', updatePosition);
    };
  }, [anchorRef, updatePosition]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent): void => {
      const target = e.target as Node | null;
      const isInsideSection =
        !!target && !!sectionRef.current?.contains(target);
      const triggerEl = (triggerRef as RefObject<HTMLElement | null>)?.current;
      const isOnTrigger =
        !!target &&
        triggerEl !== null &&
        triggerEl !== undefined &&
        typeof (triggerEl as HTMLElement).contains === 'function' &&
        (triggerEl as HTMLElement).contains(target);

      if (!isInsideSection && !isOnTrigger) {
        onClose?.();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose, triggerRef]);

  const content = (
    <div
      ref={sectionRef}
      className={twMerge(
        'fixed z-[9999] flex flex-col gap-2 bg-white border border-navy-01 rounded-md py-3 px-4 shadow-sm animate-slide-down',
        AI_ASSISTANT_CHAT_MESSAGE_MAX_WIDTH_CLASS
      )}
      style={
        position
          ? {
              top: position.top,
              left: position.left,
              width: position.width,
            }
          : { visibility: 'hidden' }
      }
    >
      <Stack
        direction="row"
        sx={{ alignItems: 'center', justifyContent: 'space-between' }}
      >
        <p className="font-semibold text-gray-900">{feedbackTypeLabel}</p>
        <Button
          variant="reserve-text"
          className="!w-7 !h-7 !p-0"
          onClick={onClose}
        >
          <Icons.Close customClass="w-4 h-4" />
        </Button>
      </Stack>

      <Stack direction="row" sx={{ gap: 1 }}>
        <MessageInput
          onSendMessage={(message: string) =>
            onSendFeedback(feedbackType, message)
          }
          placeholder="Add specific details"
          rows={3}
          maxLength={1000}
          disabled={isSendingFeedback}
        />
      </Stack>
    </div>
  );

  return createPortal(content, document.body);
};

export default FeedbackSection;
